<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import Button from 'dashboard/components-next/button/Button.vue';
import ButtonGroup from 'dashboard/components-next/buttonGroup/ButtonGroup.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useMapGetter, useStoreGetters } from 'dashboard/composables/store';
import { useCaptainIcon } from 'dashboard/composables/useCaptainIcon';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
const route = useRoute();

const { uiSettings, updateUISettings } = useUISettings();
const getters = useStoreGetters();
const { captainIconUrl, hasCaptainIcon, handleImageError } = useCaptainIcon();

const isConversationRoute = computed(() => {
  const CONVERSATION_ROUTES = [
    'inbox_conversation',
    'conversation_through_inbox',
    'conversations_through_label',
    'team_conversations_through_label',
    'conversations_through_folders',
    'conversation_through_mentions',
    'conversation_through_unattended',
    'conversation_through_participating',
    'inbox_view_conversation',
  ];
  return CONVERSATION_ROUTES.includes(route.name);
});

const currentAccountId = useMapGetter('getCurrentAccountId');
const currentUser = useMapGetter('getCurrentUser');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const currentAccount = computed(() => {
  return getters['accounts/getAccount'].value(currentAccountId.value) || {};
});

const showCopilotLauncher = computed(() => {
  const isCaptainEnabled = isFeatureEnabledonAccount.value(
    currentAccountId.value,
    FEATURE_FLAGS.CAPTAIN
  );

  const settings = currentAccount.value?.settings || {};

  // v6.4.25: Default false — bubble desativado para novas contas (requer explicit true)
  const bubbleEnabled = settings.copilot_bubble_enabled === true;

  // v6.4.25: Default true — admin-only ativado por padrão (requer explicit false para liberar)
  const adminOnly = settings.copilot_bubble_admin_only !== false;
  const userRole = currentUser.value?.role;
  const userType = currentUser.value?.type;
  const isAdminOrSuper = userRole === 'administrator' || userType === 'SuperAdmin';

  // If bubble is disabled, hide for all
  if (!bubbleEnabled) {
    return false;
  }

  // If admin-only is ON and user is not admin/superadmin, hide bubble
  if (adminOnly && !isAdminOrSuper) {
    return false;
  }

  return (
    isCaptainEnabled &&
    !uiSettings.value?.is_copilot_panel_open &&
    !isConversationRoute.value
  );
});

const toggleSidebar = () => {
  updateUISettings({
    is_copilot_panel_open: !uiSettings.value.is_copilot_panel_open,
    is_contact_sidebar_open: false,
  });
};
</script>

<template>
  <div
    v-if="showCopilotLauncher"
    class="fixed bottom-4 ltr:right-4 rtl:left-4 z-50"
  >
    <ButtonGroup
      class="rounded-full bg-n-alpha-2 backdrop-blur-lg p-1 shadow hover:shadow-md"
    >
      <button
        v-if="hasCaptainIcon"
        class="flex items-center justify-center size-10 rounded-full bg-n-solid-3 dark:bg-n-alpha-2 text-n-slate-12 transition-all duration-200 ease-out hover:brightness-110"
        @click="toggleSidebar"
      >
        <img
          :src="captainIconUrl"
          alt="Copilot"
          class="size-6 object-contain"
          @error="handleImageError"
        />
      </button>
      <Button
        v-else
        icon="i-woot-captain"
        no-animation
        class="!rounded-full !bg-n-solid-3 dark:!bg-n-alpha-2 !text-n-slate-12 text-xl transition-all duration-200 ease-out hover:brightness-110"
        lg
        @click="toggleSidebar"
      />
    </ButtonGroup>
  </div>
  <template v-else />
</template>
